import { COUNTRY_ECOTAX_TYPE } from '@wyz/types';
import { Nillable } from '~/common/types';
import {
  Badge,
  Card,
  Flex,
  FormatDate,
  Icon,
  IconButton,
  Label,
  OverlayTrigger,
  Popover,
  Text,
  Title,
  TyreInfoBox,
} from '@wyz/ui';
import {
  DateUtils,
  getTyreIdentifiers,
  useBoolean,
  useOnUpdate,
} from '@wyz/utils';
import clsx from 'clsx';
import { debounce, isNil } from 'lodash-es';
import { PropsWithChildren, useEffectEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { NumberField, PrivateComponent } from '~/common/components';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { PurchaseErrorCodes, ROLES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { IUserCustomer } from '~/common/types';
import { ValueInfoItem } from '~/purchase/components';
import { usePurchaseContext } from '~/purchase/context';
import { MIN_CART_QUANTITY } from '~/purchase/helpers';
import { usePurchaseCart } from '~/purchase/hooks';
import { ICartItem, ICartItemErrors, ICartMeta } from '~/purchase/types';

// won't remove for now
// const LAYOUT = {
//   SOURCE: '20%',
//   BF: '10%',
//   DELIVERY: '15%',
//   ECOTAX: '10%',
//   PRICE: '15%',
//   TOTAL: '15%',
// };
function ErrorIndicator({ children }: PropsWithChildren) {
  return (
    <OverlayTrigger
      trigger={['focus', 'hover']}
      placement='bottom'
      overlay={<Popover className='py-1 px-2'>{children}</Popover>}
    >
      <div>
        <Icon icon='warning-triangle' size='xxs' />
      </div>
    </OverlayTrigger>
  );
}

type Props = {
  cartItem: ICartItem;
  privileges: ICartMeta;
  onRemoveItem: (cartItemId: string) => void;
  errors?: ICartItemErrors;
  customer: IUserCustomer;
};
const CartRow = ({
  customer,
  cartItem,
  errors,
  privileges,
  onRemoveItem,
}: Props) => {
  const { t } = useI18n();
  const { cdnUrl } = useConfig();
  const locale = useLocale();
  const acl = useAccessControl();
  const methods = useFormContext<{ quantity: Record<string, number> }>();
  const [showQuantityMessage, quantityMessageHandlers] = useBoolean(false);
  const { state } = usePurchaseContext();
  const purchaseCart = usePurchaseCart(customer.id, {
    onQuantityRejected: () => quantityMessageHandlers.setTrue(),
    onItemRemoved: (cartItemId) => onRemoveItem(cartItemId),
  });
  const FIELD_NAME = `quantity.${cartItem.id}` as const;

  const onUpdateQuantity = debounce((newQuantity?: Nillable<number>) => {
    if (!methods.getFieldState(FIELD_NAME).invalid) {
      quantityMessageHandlers.setFalse();
      const nextQuantity = newQuantity || 1;
      purchaseCart.updateQuantity.process(cartItem.id, nextQuantity, {
        ean: cartItem.tyre.ean,
        brand: cartItem.tyre.brand,
        dimension: cartItem.tyre.dimension,
        unitPrice: cartItem.price.unitPrice,
      });
    }
  }, 1000);
  const hasChangedValues =
    errors &&
    isNil(errors?.[PurchaseErrorCodes.ERR_OFFER_REPLACED]) &&
    (false !== errors?.[PurchaseErrorCodes.ERR_DELIVERY_DATE_CHANGED] ||
      false !== errors?.[PurchaseErrorCodes.ERR_PRICE_CHANGED] ||
      false !== errors?.[PurchaseErrorCodes.ERR_QUANTITY_CHANGED]);
  const updateValue = useEffectEvent((quantity: number) => {
    methods.setValue(FIELD_NAME, quantity);
  });
  useOnUpdate(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    updateValue(cartItem.quantity);
  }, [cartItem.quantity]);

  const isExpressOffer = cartItem.delivery.j0Delivery;
  const isExpressFree = isExpressOffer && !cartItem.price.j0DeliveryFee;
  const withOneTyreDelivery =
    cartItem.delivery.oneTyreDelivery &&
    !isNil(cartItem.price.oneTyreDeliveryFee) &&
    cartItem.price.oneTyreDeliveryFee > 0;

  return (
    <Card
      className={clsx({
        'border border-2 border-warning-text': hasChangedValues,
      })}
    >
      <div className='bg-neutral-grey-1 p-4 rounded-top border-bottom border-neutral-gray-4'>
        <Flex justifyContent='between' wrap='wrap'>
          <TyreInfoBox
            t={t}
            tyre={{
              ...cartItem.tyre,
              ...getTyreIdentifiers(
                {
                  cai: cartItem.tyre.cai,
                  constructorReference: cartItem.tyre.constructorReference,
                  alternativeReference: cartItem.tyre.alternativeReference,
                },
                state.primaryCustomer?.settingTemplate?.generalSetting
                  .tyreIdentifierFields ?? undefined,
              ),
            }}
            imageBaseUrl={cdnUrl}
          />
          <Flex alignItems='center'>
            <div>
              <Label
                htmlFor={FIELD_NAME}
                className={clsx({ 'ms-4': showQuantityMessage })}
              >
                <Text weight='semi-bold' color='secondary-gray' heading='xs'>
                  <Trans i18nKey='app.common.quantity' />
                </Text>
              </Label>
              <Flex alignItems='center' className='position-relative'>
                {(showQuantityMessage ||
                  errors?.[PurchaseErrorCodes.ERR_QUANTITY_CHANGED]) && (
                  <ErrorIndicator>
                    <Trans
                      i18nKey='app.purchase.cart.errors.invalid_stock'
                      values={{
                        quantity:
                          errors?.[PurchaseErrorCodes.ERR_QUANTITY_CHANGED],
                      }}
                      components={[
                        <Text
                          key={0}
                          heading='xs'
                          color='secondary'
                          weight='semi-bold'
                          component='div'
                        />,
                        <Text
                          key={1}
                          heading='xs'
                          color='success'
                          weight='semi-bold'
                          component='div'
                        />,
                      ]}
                    />
                  </ErrorIndicator>
                )}
                <div
                  className={clsx('mx-1', {
                    'ms-2': showQuantityMessage,
                  })}
                  style={{
                    width: '6.5rem',
                  }}
                >
                  <NumberField
                    name={FIELD_NAME}
                    size='sm'
                    min={MIN_CART_QUANTITY}
                    max={cartItem.supplier.stock ?? 0}
                    onChangeValue={onUpdateQuantity}
                    disabled={
                      !acl.isGranted(
                        [
                          ROLES.ROLE_UPDATE_CART,
                          ROLES.ROLE_UPDATE_REDISTRIBUTION_CART,
                        ],
                        'affirmative',
                      )
                    }
                    theme={
                      errors?.[PurchaseErrorCodes.ERR_QUANTITY_CHANGED]
                        ? 'warning'
                        : undefined
                    }
                    feedbackType='tooltip'
                  />
                </div>
                <PrivateComponent
                  roles={[
                    ROLES.ROLE_DELETE_CART,
                    ROLES.ROLE_DELETE_REDISTRIBUTION_CART,
                  ]}
                  strategy='affirmative'
                >
                  <IconButton
                    id={`${cartItem.tyre.ean}-remove-from-cart`}
                    variant='link'
                    icon='delete-bin-line'
                    iconSize='sm'
                    iconAlign='middle'
                    onClick={() => {
                      purchaseCart.removeItem.process(cartItem.id, {
                        ean: cartItem.tyre.ean,
                      });
                    }}
                    isLoading={purchaseCart.removeItem.isLoading}
                  />
                </PrivateComponent>
              </Flex>
            </div>
          </Flex>
        </Flex>
      </div>
      <Flex className='p-4 w-100' justifyContent='between' wrap='wrap'>
        <ValueInfoItem
          as='div'
          title={<Trans i18nKey='app.purchase.cart_page.table.source' />}
          id={`source-${cartItem.tyre.ean}`}
        >
          <Text
            heading='normal'
            weight='bold'
            color='secondary'
            className='text-wrap'
            id={`supplier-name-${cartItem.tyre.ean}`}
          >
            {cartItem.supplier.name}
          </Text>
        </ValueInfoItem>
        {Boolean(cartItem.price.bfPrice) && (
          <ValueInfoItem
            as='div'
            title={<Trans i18nKey='app.common.price.bf' />}
            id={`bf-${cartItem.tyre.ean}`}
          >
            <Text
              heading='normal'
              weight='semi-bold'
              color='secondary'
              id={`bf-price-${cartItem.tyre.ean}`}
            >
              {locale.formatters.money(cartItem.price.bfPrice, {
                currency: cartItem.price.currency,
                fallback: '/',
              })}
            </Text>
          </ValueInfoItem>
        )}
        <ValueInfoItem
          as='div'
          title={<Trans i18nKey='app.common.estimation_delivery' />}
          id={`delivery-${cartItem.tyre.ean}`}
        >
          {errors?.ERR_GET_STOCK_PRICE_SERVER_ERROR ||
          errors?.ERR_GET_STOCK_PRICE_TIMEOUT ? (
            <Text heading='normal' weight='semi-bold' color='secondary'>
              {'/'}
            </Text>
          ) : !errors?.[PurchaseErrorCodes.ERR_DELIVERY_DATE_CHANGED] ? (
            <DeliveryValue {...{ errors, cartItem }} />
          ) : (
            <Badge bg={'warning'}>
              <DeliveryValue {...{ errors, cartItem }} />
            </Badge>
          )}
        </ValueInfoItem>
        <div style={{ width: '8%' }}></div>
        {(isExpressOffer || withOneTyreDelivery) && (
          <ValueInfoItem
            as='div'
            title={<Trans i18nKey='app.purchase.cart_page.delivery_fee' />}
            align='right'
            id={`delivery-fee-${cartItem.tyre.ean}`}
          >
            <Flex justifyContent='end' alignItems='center' className='gap-1'>
              <Text
                color='brand-blue-alt-normal'
                id={`delivery-fee-text-${cartItem.tyre.ean}`}
              >
                <Flex alignItems='center' className='gap-1'>
                  {withOneTyreDelivery && (
                    <Icon icon='football-fill' size='sm' />
                  )}
                  {isExpressOffer && (
                    <Icon
                      icon={
                        cartItem.delivery.todayDelivery
                          ? 'today-delivery-wording'
                          : 'morning-delivery-wording'
                      }
                    />
                  )}
                </Flex>
              </Text>
              <Text
                color='brand-blue-alt-normal'
                heading='normal'
                weight='semi-bold'
                id={`delivery-fee-value-${cartItem.tyre.ean}`}
              >
                {(withOneTyreDelivery || !isExpressFree) && (
                  <Flex>
                    <span translate='no'>+</span>
                    <span>
                      {locale.formatters.money(cartItem.deliveryItemUnitPrice, {
                        currency: cartItem.price.currency,
                      })}
                    </span>
                  </Flex>
                )}
                {isExpressFree && (
                  <Flex>
                    <Badge
                      bg='primary'
                      className='text-nowrap'
                      id='j0-price-badge'
                    >
                      <Trans i18nKey='app.common.free_service' />
                    </Badge>
                  </Flex>
                )}
              </Text>
            </Flex>
          </ValueInfoItem>
        )}
        {privileges.ecotaxType !== COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED && (
          <ValueInfoItem
            as='div'
            title={<Trans i18nKey='app.purchase.cart_page.ecotax_fee' />}
            align='left'
            id={`ecotax-${cartItem.tyre.ean}`}
          >
            <Text
              heading='normal'
              weight='semi-bold'
              id={`ecotax-value-${cartItem.tyre.ean}`}
            >
              {privileges.ecotaxType ===
              COUNTRY_ECOTAX_TYPE.INCLUDED_IN_TYRE ? (
                <Text heading='normal' weight='semi-bold' color='success'>
                  <Trans i18nKey='app.purchase.cart_page.ecotax_included' />
                </Text>
              ) : (
                locale.formatters.money(cartItem.price.ecoTaxPrice ?? 0, {
                  currency: cartItem.price.currency,
                })
              )}
            </Text>
          </ValueInfoItem>
        )}
        <ValueInfoItem
          as='div'
          title={<Trans i18nKey='app.purchase.cart_page.table.unit_price' />}
          align='right'
          id={`unit-price-${cartItem.tyre.ean}`}
        >
          {!errors?.[PurchaseErrorCodes.ERR_PRICE_CHANGED] ? (
            <PriceValue {...{ errors, cartItem }} />
          ) : (
            <Badge bg={'warning'}>
              <PriceValue {...{ errors, cartItem }} />
            </Badge>
          )}
        </ValueInfoItem>
        <Flex className='flex-1' justifyContent='end'>
          <ValueInfoItem
            as='div'
            title={<Trans i18nKey='app.common.total_price_without_tax' />}
            align='right'
          >
            <Title
              id={`unit-price-excl-tax-${cartItem.tyre.ean}`}
              heading='lg'
              weight='bold'
              className='text-right'
            >
              {locale.formatters.money(cartItem.itemTotal, {
                currency: cartItem.price.currency,
              })}
            </Title>
          </ValueInfoItem>
        </Flex>
      </Flex>
    </Card>
  );
};

export default CartRow;
function PriceValue({ errors, cartItem }: Pick<Props, 'errors' | 'cartItem'>) {
  const locale = useLocale();
  const { t } = useI18n();

  return (
    <Flex
      alignItems='center'
      justifyContent='end'
      className='gap-1 position-relative'
    >
      {!isNil(errors?.[PurchaseErrorCodes.ERR_PRICE_CHANGED]) &&
        errors[PurchaseErrorCodes.ERR_PRICE_CHANGED] !== false && (
          <ErrorIndicator>
            <Trans
              i18nKey='app.purchase.cart.errors.invalid_price'
              values={{ price: errors?.[PurchaseErrorCodes.ERR_PRICE_CHANGED] }}
              components={[
                <Text
                  key={0}
                  heading='xs'
                  color='secondary'
                  weight='semi-bold'
                  component='div'
                />,
                <Text
                  key={1}
                  heading='xs'
                  color='success'
                  weight='semi-bold'
                  component='div'
                />,
              ]}
            />
          </ErrorIndicator>
        )}
      <Flex direction='column'>
        <Title
          heading='lg'
          weight='bold'
          color={
            errors?.[PurchaseErrorCodes.ERR_PRICE_CHANGED]
              ? 'warning'
              : 'secondary'
          }
          id={`unit-price-value-${cartItem.tyre.ean}`}
        >
          {locale.formatters.money(cartItem.price.unitPrice, {
            currency: cartItem.price.currency,
          })}
        </Title>
        <Text
          heading='xs'
          weight='semi-bold'
          color='secondary-gray'
          className='text-right'
        >
          {t('app.purchase.bf_discount', {
            price: cartItem.price.bfDiscount,
          })}
        </Text>
      </Flex>
    </Flex>
  );
}
function DeliveryValue({
  errors,
  cartItem,
}: Pick<Props, 'errors' | 'cartItem'>) {
  const locale = useLocale();
  const error = errors?.[PurchaseErrorCodes.ERR_DELIVERY_DATE_CHANGED];
  const backOrderError = errors?.[PurchaseErrorCodes.ERR_BACKORDER_CHANGED];
  const { t } = useI18n();
  return (
    <Flex alignItems='center' className='gap-1 position-relative'>
      <Title
        heading='lg'
        weight='bold'
        color={error ? 'warning' : 'secondary'}
        id={`delivery-date-${cartItem.tyre.ean}`}
      >
        {cartItem.delivery.backOrder ? (
          <Trans i18nKey='app.common.offer_type.backorder' />
        ) : (
          <FormatDate
            date={cartItem.delivery.date}
            format={DateUtils.DATE_FORMAT.DM}
            locale={locale.locale}
          />
        )}
      </Title>
      {error && (
        <ErrorIndicator>
          <Text
            heading='xs'
            weight='normal'
            color='secondary'
            id={`delivery-error-${cartItem.tyre.ean}`}
          >
            <Trans
              i18nKey='app.purchase.cart.errors.invalid_date'
              values={{
                date:
                  backOrderError && !cartItem.delivery.backOrder
                    ? t('app.common.offer_type.backorder')
                    : locale.formatters.date(new Date(error), {
                        format: DateUtils.DATE_FORMAT.DM,
                      }),
              }}
              components={[
                <Text
                  key={0}
                  heading='xs'
                  color='secondary'
                  weight='semi-bold'
                  component='div'
                />,
                <Text
                  key={1}
                  heading='xs'
                  color='success'
                  weight='semi-bold'
                  component='div'
                />,
              ]}
            />
          </Text>
        </ErrorIndicator>
      )}
    </Flex>
  );
}
